import React from 'react';
import axios from 'axios';
import { createRoot } from 'react-dom/client';
import App from './App';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { store, persistor } from './app/store';
import { Auth0Provider } from '@auth0/auth0-react';
import { AuthProvider } from './react-oauth2-code-pkce/AuthContext';
import type { TAuthConfig, TRefreshTokenExpiredEvent } from './react-oauth2-code-pkce';
import { getConfig } from './config/config-helper';
import reportWebVitals from './reportWebVitals';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import './index.css';
import LoginView from 'views/LoginView';
import Auth0CallbackView from 'views/Auth0CallbackView';
import PlatformAuthCallbackView from 'views/PlatformAuthCallbackView';
import SharedAuthCallbackView from 'views/SharedAuthCallbackView';
import AdminView from 'views/AdminView';
import UserManagementView from 'views/UserManagementView';
import UserManagementUsersTab from 'views/UserManagementUsersTab';
import UserManagementGroupsTab from 'views/UserManagementGroupsTab';
import UserManagementRolesTab from 'views/UserManagementRolesTab';
import UserManagementScimManagersTab from 'views/UserManagementScimManagersTab';
import ErrorView from 'views/ErrorView';
import NotFoundView from 'views/NotFoundView';
import PublicRoute from 'features/authentication/components/PublicRoute';
import Auth0ProtectedRoute from 'features/authentication/components/Auth0ProtectedRoute';
import PlatformAuthProtectedRoute from 'features/authentication/components/PlatformAuthProtectedRoute';
import SnowflakeAccountsView from 'views/SnowflakeAccountsView';
import OauthIntegrationsView from 'views/OauthIntegrationsView';
import SnowflakeAccountLoginView from 'views/SnowflakeAccountLoginView';
import LastVisitedUrlTracker from 'components/LastVisitedUrlTracker';

// Import new frictionless signup components
import SignupView from 'views/SignupView';
import OnboardingView from 'views/OnboardingView';
import RunnersView from 'views/RunnersView';
import DashboardView from 'views/DashboardView';
import ScimManagementView from 'views/ScimManagementView';

// Import ReCaptcha provider
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

axios.defaults.withCredentials = true;
axios.defaults.xsrfHeaderName = 'X-CSRFToken';
axios.defaults.xsrfCookieName = 'csrftoken';

const { dataopsOAuthClient, recaptchaSiteKey, posthog: posthogConfig } = getConfig();

// Initialize PostHog
if (posthogConfig.apiKey !== '') {
  posthog.init(posthogConfig.apiKey, {
    api_host: posthogConfig.host,
    person_profiles: 'identified_only',
  });
}
const { authorizationEndpoint, tokenEndpoint, redirectUri, scope, decodeToken, redirectCallbackPath, autoLogin } =
  dataopsOAuthClient;

const authConfig: TAuthConfig = {
  clientId: dataopsOAuthClient.clientId,
  authorizationEndpoint,
  tokenEndpoint,
  redirectUri,
  scope,
  decodeToken,
  redirectCallbackPath,
  autoLogin,
  onRefreshTokenExpire: (event: TRefreshTokenExpiredEvent) => {
    window.confirm('Session expired. Refresh page to continue using the site?') && event.login();
  },
};

// NEW: Create public router outside of Auth0Provider
const publicRouter = createBrowserRouter([
  {
    path: '/snowflake-accounts/auth',
    element: <SharedAuthCallbackView />,
  },
  {
    path: '/snowflake-accounts/integrations/:id/auth',
    element: <SharedAuthCallbackView />,
  },
  // NEW: Public signup route with ReCaptcha provider
  {
    path: '/signup',
    element: (
      <GoogleReCaptchaProvider reCaptchaKey={recaptchaSiteKey} language="en">
        <PublicRoute allowUnauthenticated={true}>
          <SignupView />
        </PublicRoute>
      </GoogleReCaptchaProvider>
    ),
  },
]);

// Main authenticated router (existing structure)
const authenticatedRouter = createBrowserRouter([
  {
    path: '/platform-auth',
    element: (
      <AuthProvider authConfig={authConfig}>
        <LastVisitedUrlTracker />
        <PlatformAuthProtectedRoute>
          <App />
        </PlatformAuthProtectedRoute>
      </AuthProvider>
    ),
    children: [
      { path: '/platform-auth/snowflake-accounts/auth', element: <SnowflakeAccountLoginView authType="platform" /> },
      {
        path: '/platform-auth/snowflake-accounts/integrations/:id/auth',
        element: <SnowflakeAccountLoginView authType="platform" />,
      },
    ],
  },
  {
    path: '/dataops-oauth-callback',
    element: (
      <AuthProvider authConfig={authConfig}>
        <PlatformAuthCallbackView />
      </AuthProvider>
    ),
  },
  {
    path: '/snowflake-accounts/integrations/:id/auth',
    element: <SharedAuthCallbackView />,
  },
  {
    path: '/',
    element: (
      <Auth0Provider
        domain={process.env.REACT_APP_AUTH0_DOMAIN}
        clientId={process.env.REACT_APP_AUTH0_ADMIN_UI_CLIENT_ID}
        skipRedirectCallback={
          window.location.pathname === '/snowflake-accounts/auth' ||
          window.location.pathname === '/auth0/snowflake-accounts/auth'
        }
        cacheLocation="localstorage"
        authorizationParams={{
          redirect_uri: `${window.location.origin}/react-callback`,
          audience: 'https://admin.dataops.live/customer_admin/',
        }}
      >
        <App />
      </Auth0Provider>
    ),
    errorElement: <ErrorView />,
    children: [
      {
        path: '/',
        element: (
          <Auth0ProtectedRoute>
            <AdminView />
          </Auth0ProtectedRoute>
        ),
        children: [
          {
            path: '/',
            element: <DashboardView />,
          },
          {
            path: '/snowflake-accounts',
            element: <SnowflakeAccountsView />,
          },
          {
            path: '/snowflake-accounts/integrations',
            element: <OauthIntegrationsView />,
          },
          {
            path: '/auth0/snowflake-accounts/auth',
            element: <SnowflakeAccountLoginView authType="auth0" />,
          },
          {
            path: '/auth0/snowflake-accounts/integrations/:id/auth',
            element: <SnowflakeAccountLoginView authType="auth0" />,
          },
          {
            path: '/user-management',
            element: <UserManagementView />,
            children: [
              {
                path: '/user-management',
                element: <UserManagementUsersTab />,
              },
              {
                path: '/user-management/groups',
                element: <UserManagementGroupsTab />,
              },
              {
                path: '/user-management/roles',
                element: <UserManagementRolesTab />,
              },
              {
                path: '/user-management/scim-managers',
                element: <UserManagementScimManagersTab />,
              },
            ],
          },
          {
            path: '/runners',
            element: <RunnersView />,
          },
          {
            path: '/scim-management',
            element: <ScimManagementView />,
          },
        ],
      },
      {
        path: '/react-callback',
        element: <Auth0CallbackView />,
      },
      {
        path: '/login',
        element: (
          <PublicRoute>
            <LoginView />
          </PublicRoute>
        ),
      },
      {
        path: '/onboarding',
        element: (
          <Auth0ProtectedRoute>
            <OnboardingView />
          </Auth0ProtectedRoute>
        ),
      },
      {
        path: '*',
        element: <NotFoundView />,
      },
    ],
  },
]);

// Combine both routers - determine which to use based on current path
const getRouter = (): ReturnType<typeof createBrowserRouter> => {
  const isPublicRoute = ['/signup', '/snowflake-accounts/auth'].some((route) =>
    window.location.pathname.startsWith(route),
  );
  return isPublicRoute ? publicRouter : authenticatedRouter;
};

const container = document.getElementById('root');

if (container != null) {
  const root = createRoot(container);

  root.render(
    <React.StrictMode>
      <PostHogProvider client={posthog}>
        <Provider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <RouterProvider router={getRouter()} />
          </PersistGate>
        </Provider>
      </PostHogProvider>
    </React.StrictMode>,
  );
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
